/**
 * AdvisorInviteDashboardCard — WL-GTM-7 "Kanzlei einladen"-CTA, placement (a)
 * "Dashboard-Karte" (modules-loop-b.md WL-GTM-7, build-plan-loop-b.md §A.5
 * Gate-Sitz "WL-K8"). Bestandsnutzer → Kanzlei-Leads über den EXISTIERENDEN
 * owner→advisor-Invite (`/dashboard/konto/steuerberater`).
 *
 * Outer/Inner split (pattern: `KanzleiUebergabeMonthEndBanner`) — the outer
 * component is the cheap, synchronous visibility gate (beta + owner + own
 * scope + not permanently dismissed); the inner component is only mounted
 * once those pass, and itself defers to `useAdvisorInviteFunnelStatus` for
 * the one async check (does the owner already have a pending/accepted
 * Kanzlei-invite? if so, this CTA is pointless and stays hidden).
 *
 * Gating (spec, WL-GTM-7): "nur im Loop-B-Beta-Kontext + nur für Owner, die
 * noch keine aktive Kanzlei-Verknüpfung/kein offenes Advisor-Invite haben".
 * Rein kosmetisches Client-Gating (wie jedes Loop-B-FE-Gate) — kein
 * Zugriffsschutz, die harten Sperren bleiben server-seitig unverändert.
 *
 * No dark pattern (Reviewer-Gate): sachlich formuliert, dismissbar (X →
 * `advisorInviteCtaSuppression`, PERMANENT — kein Nag-Loop, anders als der
 * 7-Tage-Cooldown des WL-F1-Paywalls), keine Fake-Dringlichkeit.
 *
 * @module components/tenant/AdvisorInviteDashboardCard
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconClose, IconAdvisor } from '../../icons';

import { useAuth } from '../../context/AuthContext';
import { canInviteAdvisor } from '../../config/advisorInviteAccess';
import { useAdvisorInviteFunnelStatus } from '../../hooks/useAdvisorInviteFunnelStatus';
import { useIsAdvisorContext } from '../../hooks/useIsAdvisorContext';
import {
  dismissAdvisorInviteCta,
  isAdvisorInviteCtaDismissed,
} from '../../utils/advisorInviteCtaSuppression';

/**
 * Inner component — only mounted once the outer gate passes. Resolves the
 * owner's own invite-funnel status; renders nothing until it positively
 * knows there is no pending/accepted Kanzlei-invite yet.
 */
const AdvisorInviteDashboardCardInner: React.FC = () => {
  const { t } = useTranslation('common');
  const [closed, setClosed] = useState(false);
  const { canShowInviteCta } = useAdvisorInviteFunnelStatus(true);

  if (closed || !canShowInviteCta) return null;

  const handleDismiss = (): void => {
    dismissAdvisorInviteCta('dashboard_card');
    setClosed(true);
  };

  return (
    <div
      role="status"
      className="mb-4 flex flex-col gap-3 rounded-lg border border-emerald-600/50 bg-[var(--tone-success-bg)] p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)]">
          <IconAdvisor size={16} />
        </span>
        <div>
          <p className="text-sm font-semibold text-[var(--tone-success-fg)]">{t('tenant.gtm7.dashboardTitle')}</p>
          <p className="mt-0.5 text-xs text-[var(--tone-success-fg)]">{t('tenant.gtm7.dashboardMessage')}</p>
        </div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2 self-end sm:self-auto">
        <Link
          to="/dashboard/konto/steuerberater"
          className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-500"
        >
          {t('tenant.gtm7.cta')}
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('tenant.gtm7.dismiss')}
          className="text-[var(--tone-success-fg)] transition-colors hover:text-primary"
        >
          <IconClose size={14} />
        </button>
      </div>
    </div>
  );
};

/**
 * Outer visibility gate. Fail-closed: renders `null` unless EVERY cheap,
 * synchronous condition holds — Loop-B-beta, owner (not an advisor viewing a
 * mandant), viewing the user's OWN scope, not already permanently dismissed
 * on this device, AND the user is not a Steuerberater in any sense (WL-GTM-7
 * follow-up: `activeRole !== 'advisor'` alone is insufficient — a Kanzlei
 * that switched into its own owner tenant still has `activeRole === 'owner'`
 * but should never see a "Kanzlei einladen" CTA; a Kanzlei doesn't invite a
 * Kanzlei). `useIsAdvisorContext()` is a hook — called unconditionally at
 * the top of the component, never short-circuited.
 */
const AdvisorInviteDashboardCard: React.FC = () => {
  const { currentUser, sellerData, activeRole, effectiveScopeUid } = useAuth();
  const isAdvisor = useIsAdvisorContext();

  const isOwnScope = !!currentUser && effectiveScopeUid === currentUser.uid;
  const eligible =
    canInviteAdvisor(currentUser?.uid, sellerData) &&
    activeRole !== 'advisor' &&
    isOwnScope &&
    !isAdvisorInviteCtaDismissed('dashboard_card') &&
    !isAdvisor;

  if (!eligible) return null;
  return <AdvisorInviteDashboardCardInner />;
};

export default AdvisorInviteDashboardCard;
